import { ChevronDownIcon, CheckCircleIcon, XCircleIcon, ClockIcon, ForwardIcon } from "../../../../automationStudioIcons";
import { useState, useMemo, useCallback } from "react";
import ImagePreviewModal from "../../../../../Modal/ImagePreviewModal";
import { EXECUTION_STATUS } from "../../../../../AutomationStudio/hooks/useReplayExecution";
import styles from "../../../../../../styles/automationStudio/automationStudio.module.scss";
import { LABELS } from "../../../../content";
import { EVALUATION_TOOL } from "../../../../../AutomationStudio/constant";
import { formatDuration, getFlatStepIndex } from "../../../../../AutomationStudio/utils";
import { extractAutomationScreenshotPath, resolveScreenshotUrl, refreshScreenshotUrl } from "../../../../../../utils/screenshotUtils";
import AutomationText from "../../../shared/AutomationText";
import {
    getReplayStepDisplayDescription,
    getReplayStepToolForDisplay,
    getReplayUserFacingErrorMessage,
    getEvaluationNarrativeFromPlanStep,
    getEvaluationNarrativeFromExecDetail,
} from "../../../../../AutomationStudio/utils/stepExecutionDisplay";
import useAutomationTextFormatter from "../../../../../AutomationStudio/hooks/useAutomationTextFormatter";
import {
    isRecordedPlanContext,
    isSavedRecordedPlanIdleView,
    shouldShowRecordingSkipUi,
    shouldSuppressRecordingOutcomeDuringReplay,
    getRecordingSkipReason,
    mapRecordingStatusForDisplay,
} from "../../../../../AutomationStudio/utils/replayEligibility";
import { resolveReplayStepStatus } from "../../../../../AutomationStudio/utils/replayProgressDisplay";
import ResolvedScreenshot from "./ResolvedScreenshot";
import ScreenshotPlaceholder from "../../../shared/ScreenshotPlaceholder";
import { getInstructionsWithStepsForResults } from "../../../leftPanel/steps/instructionHierarchyUtils";

/**
 * StepResultsTab - Hierarchical step results grouped by instruction
 * Uses replayProgressMap when replay data exists for accurate status display
 */
export default function StepResultsTab({
    instructions = [],
    replayProgressMap = {},
    replayExecutionResults = null,
    flatStepsForReplay = [],
    onStepClick = null,
    isReplayFlow = false,
    hasExecutedInSession = false,
    isRunning = false,
    isReplayExecuting = false,
    isGenerating = false,
}) {
    const isSavedPlanIdle = useMemo(
        () => isSavedRecordedPlanIdleView({
            instructions,
            hasExecutedInSession,
            isRunning,
            isReplayExecuting,
            isGenerating,
        }),
        [instructions, hasExecutedInSession, isRunning, isReplayExecuting, isGenerating]
    );
    const isLiveRunActive = isRunning && !isReplayExecuting;

    const hasReplayData = isReplayFlow;
    const isRecordedPlan = isRecordedPlanContext(instructions);
    const resultsInstructions = useMemo(
        () => getInstructionsWithStepsForResults(instructions),
        [instructions]
    );
    const { format } = useAutomationTextFormatter();
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewRawPath, setPreviewRawPath] = useState(null);
    const [previewTitle, setPreviewTitle] = useState("Step screenshot");
    const [resolvedPreviewUrl, setResolvedPreviewUrl] = useState(null);

    const openScreenshotPreview = useCallback(async (rawPath, title) => {
        if (!rawPath || typeof rawPath !== 'string' || !rawPath.trim()) return;
        setPreviewRawPath(rawPath);
        setPreviewTitle(title || 'Step screenshot');
        setResolvedPreviewUrl(null);
        setIsPreviewOpen(true);
        try {
            const { url } = await resolveScreenshotUrl(rawPath);
            setResolvedPreviewUrl(url || null);
        } catch {
            setResolvedPreviewUrl(null);
        }
    }, []);

    const handlePreviewImageError = useCallback(async () => {
        if (!previewRawPath) return null;
        const { url } = await refreshScreenshotUrl(previewRawPath);
        if (url) {
            setResolvedPreviewUrl(url);
            return url;
        }
        return null;
    }, [previewRawPath]);

    const closePreview = () => {
        setIsPreviewOpen(false);
        setPreviewRawPath(null);
        setResolvedPreviewUrl(null);
    };

    // Resolve stepIndex for stepDetails lookup: prefer step.id match in flatStepsForReplay so screenshots
    // stay correctly associated when plan/instructions ordering differs (e.g. loaded from test case).
    const getStepIndexForLookup = (step, flatStepIndex) => {
        const fromPlan = flatStepsForReplay?.find((s) => s?.id === step.id);
        return fromPlan?.stepIndex ?? flatStepIndex;
    };

    // Map EXECUTION_STATUS to display
    const getReplayStatusDisplay = (replayStatus) => {
        if (!replayStatus || replayStatus === EXECUTION_STATUS.NEW) {
            return { icon: <ClockIcon className={styles.stepResultIconPending} />, color: '#64748B' };
        }
        switch (replayStatus) {
            case EXECUTION_STATUS.PASSED:
                return { icon: <CheckCircleIcon className={styles.stepResultIconSuccess} />, color: '#16A34A' };
            case EXECUTION_STATUS.FAILED:
                return { icon: <XCircleIcon className={styles.stepResultIconFailed} />, color: '#DC2626' };
            case EXECUTION_STATUS.SKIPPED:
                return { icon: <ForwardIcon className={styles.stepResultIconSkipped} />, color: '#6B7280' };
            case EXECUTION_STATUS.IN_PROGRESS:
                return { icon: <span className={`${styles.asSpinner} ${styles.asSpinnerSm}`} aria-hidden role="presentation" />, color: '#2563EB' };
            case EXECUTION_STATUS.PENDING:
            default:
                return { icon: <ClockIcon className={styles.stepResultIconPending} />, color: '#64748B' };
        }
    };

    // Get step status icon and color (instruction status or replay status)
    const getStepStatusDisplay = (status, replayStatus, isRecordingSkipStep, suppressRecordedOutcome) => {
        if (isSavedPlanIdle) {
            return { icon: null, color: '#64748B' };
        }
        if (isRecordingSkipStep) {
            return getReplayStatusDisplay(EXECUTION_STATUS.SKIPPED);
        }
        if (suppressRecordedOutcome) {
            return { icon: null, color: '#64748B' };
        }
        if (hasReplayData && replayStatus) {
            if (replayStatus === EXECUTION_STATUS.PENDING) {
                return { icon: null, color: '#64748B' };
            }
            return getReplayStatusDisplay(replayStatus);
        }
        switch (mapRecordingStatusForDisplay(status) || status) {
            case 'success':
            case 'completed':
                return {
                    icon: <CheckCircleIcon className={styles.stepResultIconSuccess} />,
                    color: '#16A34A'
                };
            case 'failed':
                return {
                    icon: <XCircleIcon className={styles.stepResultIconFailed} />,
                    color: '#DC2626'
                };
            default:
                return {
                    icon: <ClockIcon className={styles.stepResultIconPending} />,
                    color: '#64748B'
                };
        }
    };

    return (
        <div className={styles.stepResultsTabContainer}>
            {resultsInstructions.length === 0 ? (
                <div className={styles.emptyState}>
                    <p className={`${styles.asTypoBody2} ${styles.asTextSecondary}`}>
                        {LABELS.NO_EXECUTION_RESULTS}
                    </p>
                </div>
            ) : (
                resultsInstructions.map((instruction, resultIdx) => {
                    const displayInstructionNumber = resultIdx + 1;

                    return (
                    <details
                        key={instruction.id}
                        open
                        className={`${styles.asAccordion} ${styles.instructionAccordion}`}
                    >
                        <summary className={`${styles.asAccordionSummary} ${styles.instructionAccordionHeader}`}>
                            <div className={styles.instructionHeaderContent}>
                                <span className={`${styles.instructionTitle} ${styles.asTypoSubtitle2}`}>
                                    Instruction {displayInstructionNumber}
                                </span>
                                <AutomationText
                                    as="p"
                                    text={instruction.text ?? ''}
                                    className={`${styles.instructionText} ${styles.asTypoBody2}`}
                                />
                                {instruction.aiSteps && (
                                    <span className={`${styles.instructionStepCount} ${styles.asTypoCaption}`}>
                                        {instruction.aiSteps.length} steps
                                    </span>
                                )}
                            </div>
                            <ChevronDownIcon className={styles.asAccordionChevron} style={{ width: '16px', height: '16px' }} aria-hidden />
                        </summary>

                        <div className={`${styles.asAccordionBody} ${styles.instructionAccordionContent}`}>
                            {!instruction.aiSteps || instruction.aiSteps.length === 0 ? (
                                <span className={`${styles.asTypoCaption} ${styles.asTextSecondary}`}>
                                    {LABELS.NO_STEPS_GENERATED}
                                </span>
                            ) : (
                                <div className={styles.stepResultsList}>
                                    {instruction.aiSteps.map((step, stepIdx) => {
                                        const flatStepIndex = getFlatStepIndex(resultsInstructions, resultIdx, stepIdx);
                                        const stepIndexForLookup = getStepIndexForLookup(step, flatStepIndex);
                                        const replayStatus = hasReplayData
                                            ? resolveReplayStepStatus(step, stepIndexForLookup, replayProgressMap, true, instructions)
                                            : null;
                                        const isRecordingSkipStep = shouldShowRecordingSkipUi(step, instructions, {
                                            isReplayFlow,
                                            isSavedPlanIdle,
                                            isLiveRunActive,
                                            replayStatus,
                                        });
                                        const suppressRecordedOutcome =
                                            (isSavedPlanIdle && !isRecordingSkipStep) ||
                                            shouldSuppressRecordingOutcomeDuringReplay(
                                                step,
                                                instructions,
                                                replayStatus,
                                                { isReplayFlow }
                                            );
                                        const statusDisplay = getStepStatusDisplay(
                                            step.status,
                                            replayStatus,
                                            isRecordingSkipStep,
                                            suppressRecordedOutcome
                                        );
                                        const stepDetails = replayExecutionResults?.stepDetails?.[stepIndexForLookup];
                                        // Treat only explicit AI evaluation steps as "AI" here
                                        const isAIGeneratedStep =
                                            step.tool === EVALUATION_TOOL &&
                                            !step?.useAssertionCode;
                                        const replayResultInner = stepDetails?.result?.result;
                                        const hasExecutionData = hasReplayData
                                            ? Boolean(
                                                stepDetails?.screenshot ||
                                                stepDetails?.result?.screenshot ||
                                                replayResultInner?.screenshot ||
                                                stepDetails?.duration ||
                                                stepDetails?.result?.duration ||
                                                replayResultInner?.duration ||
                                                stepDetails?.result?.toolCallTrace ||
                                                replayResultInner?.toolCallTrace ||
                                                (replayResultInner != null &&
                                                    typeof replayResultInner.success === 'boolean')
                                            )
                                            : Boolean(
                                                step.duration ||
                                                (step.screenshot || step.executionGroup?.mainAction?.details?.screenshotPath)
                                            );
                                        const isCompletedReplay = [
                                            EXECUTION_STATUS.PASSED,
                                            EXECUTION_STATUS.FAILED,
                                            EXECUTION_STATUS.SKIPPED
                                        ].includes(replayStatus);
                                        const isSkipped = isRecordingSkipStep || replayStatus === EXECUTION_STATUS.SKIPPED;
                                        const isSkeleton = hasReplayData && (!isCompletedReplay || isSkipped) && !hasExecutionData && !suppressRecordedOutcome;
                                        const rawScreenshotPath = !isSkipped
                                            ? extractAutomationScreenshotPath(stepDetails, step)
                                            : null;
                                        const isCompletedRecordingStep =
                                            !hasReplayData &&
                                            !suppressRecordedOutcome &&
                                            ['success', 'completed', 'failed'].includes(
                                                mapRecordingStatusForDisplay(step.status) || step.status
                                            );
                                        const showNoScreenshotPlaceholder =
                                            !isSkeleton &&
                                            !rawScreenshotPath &&
                                            (isCompletedReplay || isCompletedRecordingStep);
                                        const execDuration = !isSkipped
                                            ? (
                                                stepDetails?.duration ??
                                                stepDetails?.result?.duration ??
                                                stepDetails?.result?.result?.duration ??
                                                (hasReplayData ? undefined : step.duration)
                                            )
                                            : null;
                                        const execConfidence = !isSkipped
                                            ? (stepDetails?.result?.result?.confidence ??
                                               stepDetails?.result?.details?.confidence)
                                            : null;
                                        const displayConfidence = hasReplayData ? execConfidence : (execConfidence ?? step.confidence);
                                        const confidencePct =
                                            displayConfidence != null
                                                ? (displayConfidence <= 1
                                                    ? Math.round(displayConfidence * 100)
                                                    : Math.round(displayConfidence))
                                                : null;
                                        // Match Generate AI + Script Manager: show AI % for evaluateTestStep whenever we have it (replay or generate).
                                        const showConfidence = isAIGeneratedStep && confidencePct != null;

                                        const displayTool =
                                            hasReplayData
                                                ? getReplayStepToolForDisplay(stepDetails, step) ?? step.tool
                                                : step.tool;
                                        const replayFailedMsg =
                                            hasReplayData && replayStatus === EXECUTION_STATUS.FAILED
                                                ? getReplayUserFacingErrorMessage(stepDetails)
                                                : null;
                                        const planFailedMsg =
                                            !hasReplayData && step.status === 'failed' && step.error
                                                ? step.error.message || step.error
                                                : null;
                                        const stepErrorDisplay = replayFailedMsg || planFailedMsg;
                                        const evaluationNarrative = hasReplayData
                                            ? getEvaluationNarrativeFromExecDetail(stepDetails)
                                            : getEvaluationNarrativeFromPlanStep(step);
                                        const successSummary = hasReplayData
                                            ? (
                                                replayStatus === EXECUTION_STATUS.PASSED
                                                    ? getEvaluationNarrativeFromExecDetail(stepDetails) || step.resultMessage
                                                    : null
                                            )
                                            : (
                                                step.resultMessage ||
                                                (step.status === 'success' ? evaluationNarrative : null)
                                            );
                                        const showStepError =
                                            !isSkeleton &&
                                            !isSkipped &&
                                            ((hasReplayData && replayStatus === EXECUTION_STATUS.FAILED) ||
                                                (!hasReplayData && step.status === 'failed')) &&
                                            Boolean(stepErrorDisplay);

                                        const stepLineRaw = getReplayStepDisplayDescription(
                                            step,
                                            hasReplayData ? stepDetails : null
                                        );
                                        const stepLineDisplay = format(stepLineRaw);
                                        const isSkipResultRow = isRecordingSkipStep || replayStatus === EXECUTION_STATUS.SKIPPED;
                                        const recordingSkipTooltip = isRecordingSkipStep
                                            ? getRecordingSkipReason(step.status)
                                            : undefined;

                                        return (
                                            <div
                                                key={step.id}
                                                className={`${styles.stepResultCard} ${
                                                    onStepClick ? styles.stepResultCardClickable : ''
                                                } ${isSkipResultRow ? styles.recordingSkipStepCard : ''} ${recordingSkipTooltip ? styles.tooltipHost : ''}`}
                                                title={recordingSkipTooltip}
                                                onClick={() => onStepClick && onStepClick(step)}
                                                role={onStepClick ? 'button' : undefined}
                                                tabIndex={onStepClick ? 0 : undefined}
                                                onKeyDown={onStepClick ? (e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        onStepClick(step);
                                                    }
                                                } : undefined}
                                            >
                                                {/* Step Number & Status Icon */}
                                                <div className={styles.stepResultHeader}>
                                                    <div className={styles.stepNumberBadge}>
                                                        {step.stepIndex + 1}
                                                    </div>
                                                    {!isSkeleton && statusDisplay.icon}
                                                </div>

                                                {/* Step Content */}
                                                <div className={styles.stepResultContent}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                        <span className={`${styles.stepResultDescription} ${styles.asTypoBody2}`}>
                                                            {stepLineDisplay}
                                                        </span>
                                                        {!isSkeleton && isAIGeneratedStep && (
                                                            <span className={styles.aiLabel}>
                                                                AI
                                                            </span>
                                                        )}
                                                        {!isSkeleton && isSkipped && (
                                                            <span className={`${styles.metaItem} ${styles.asTypoCaption}`} style={{ color: '#6B7280' }}>
                                                                {isRecordingSkipStep ? 'Skipped — will not run during replay' : 'Skipped'}
                                                            </span>
                                                        )}
                                                        {!isSkeleton && stepDetails?.result?.selfHealed && (
                                                            <span className={styles.healedBadge}>
                                                                🔧 Healed
                                                            </span>
                                                        )}
                                                    </div>

                                                    {!isSkeleton && (
                                                        <>
                                                            <div className={styles.stepResultMeta}>
                                                                <span className={`${styles.metaItem} ${styles.asTypoCaption}`}>
                                                                    <strong>Tool:</strong> {displayTool ?? '—'}
                                                                </span>
                                                                {stepDetails?.result?.selfHealed && stepDetails.result.healMethod && (
                                                                    <span className={`${styles.metaItem} ${styles.asTypoCaption}`} style={{ color: '#92400E' }}>
                                                                        <strong>Healed via:</strong> {stepDetails.result.healMethod}
                                                                        {stepDetails.result.healedRef && ` · ref=${stepDetails.result.healedRef}`}
                                                                    </span>
                                                                )}
                                                                {execDuration != null && (
                                                                    <span className={`${styles.metaItem} ${styles.asTypoCaption}`}>
                                                                        <strong>Duration:</strong> {formatDuration(execDuration)}
                                                                    </span>
                                                                )}
                                                                {showConfidence && (
                                                                    <span className={`${styles.metaItem} ${styles.asTypoCaption}`}>
                                                                        <strong>Confidence:</strong> {confidencePct}%
                                                                    </span>
                                                                )}
                                                            </div>

                                                            {showStepError && (
                                                                <div className={styles.stepErrorMessage}>
                                                                    <span
                                                                        className={`${styles.errorText} ${styles.asTypoCaption}`}
                                                                        title={format(stepErrorDisplay ?? '')}
                                                                    >
                                                                        {format(stepErrorDisplay ?? '')}
                                                                    </span>
                                                                </div>
                                                            )}

                                                            {step.status === 'success' && successSummary && !suppressRecordedOutcome && (
                                                                <div className={styles.stepSuccessMessage}>
                                                                    <span
                                                                        className={`${styles.successText} ${styles.asTypoCaption}`}
                                                                        title={format(successSummary)}
                                                                    >
                                                                        ✓ {format(successSummary)}
                                                                    </span>
                                                                </div>
                                                            )}

                                                        </>
                                                    )}
                                                </div>

                                                {/* Screenshot Thumbnail - use resolveScreenshotUrl for S3/relative paths */}
                                                {!isSkeleton && rawScreenshotPath && (
                                                    <div
                                                        className={styles.stepResultThumbnail}
                                                        role="button"
                                                        tabIndex={0}
                                                        aria-label="Open screenshot preview"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openScreenshotPreview(
                                                                rawScreenshotPath,
                                                                stepLineDisplay || 'Step screenshot'
                                                            );
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                openScreenshotPreview(
                                                                    rawScreenshotPath,
                                                                    stepLineDisplay || 'Step screenshot'
                                                                );
                                                            }
                                                        }}
                                                    >
                                                        <ResolvedScreenshot
                                                            rawPath={rawScreenshotPath}
                                                            alt="Step screenshot"
                                                            className={styles.thumbnailImage}
                                                        />
                                                    </div>
                                                )}
                                                {!isSkeleton && !rawScreenshotPath && showNoScreenshotPlaceholder && (
                                                    <div className={styles.stepResultThumbnail}>
                                                        <ScreenshotPlaceholder variant="empty" size="thumbnail" />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </details>
                    );
                })
            )}

            {/* Screenshot Modal - shared ImagePreviewModal (same as Screenshots tab) */}
            <ImagePreviewModal
                open={isPreviewOpen}
                onClose={closePreview}
                imageUrl={resolvedPreviewUrl}
                fileName={previewTitle}
                onImageError={handlePreviewImageError}
            />
        </div>
    );
}

